import { useState, type FormEvent } from 'react';
import { useSendInvoice } from '@repo/queries';
import type { Invoice } from '@repo/api-client/dtos';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';

interface SendInvoiceDialogProps {
  invoice: Invoice;
  defaultRecipient?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful send (e.g. navigate to celebration screen). */
  onSent?: () => void;
}

export function SendInvoiceDialog({
  invoice,
  defaultRecipient,
  open,
  onOpenChange,
  onSent,
}: SendInvoiceDialogProps) {
  const { sendInvoice, loading } = useSendInvoice();
  const { toast } = useToast();
  const [recipient, setRecipient] = useState(defaultRecipient ?? '');
  const [message, setMessage] = useState('');

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    try {
      await sendInvoice({
        invoiceId: invoice.id,
        ...(recipient ? { recipient } : {}),
        ...(message ? { message } : {}),
      });
      onOpenChange(false);
      if (onSent) {
        onSent();
      } else {
        toast({
          title: 'Invoice sent',
          description: `${invoice.invoiceNumber} is on its way.`,
          variant: 'success',
        });
      }
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send {invoice.invoiceNumber}</DialogTitle>
          <DialogDescription>
            We'll email a PDF copy and a hosted link to the recipient. The invoice
            transitions to <strong>sent</strong> once the email is delivered.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Field
            label="Recipient"
            htmlFor="recipient"
            hint="Leave blank to use the email on file."
          >
            <Input
              id="recipient"
              type="email"
              autoComplete="email"
              placeholder={defaultRecipient ?? 'client@example.com'}
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
            />
          </Field>
          <Field label="Personal note" htmlFor="message">
            <Textarea
              id="message"
              rows={4}
              placeholder="Optional. Lands at the top of the email body."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              Send invoice
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
